/**
 * ApprovalChainCard — firmenweit konfigurierbare Urlaubs-Freigabekette (Variante B).
 *
 * Wenn aktiv, ersetzt die konfigurierte Kette die Standardregel
 * (Standortleiter → Niederlassungsleiter). Pro Stufe wählbar: Typ
 * (Standortleiter / Niederlassungsleiter / Prokurist+GF / beliebige Rolle),
 * eigenes Label und „alle müssen zustimmen / einer reicht".
 * Pro-Mitarbeiter-Ausnahmen im Profil haben weiterhin Vorrang.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { GitBranch, Loader2, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { companyAPI, permissionsAPI } from '../../lib/api';

const STEP_TYPES = [
  { value: 'location_manager', label: 'Standortleiter/in (des Mitarbeiters)' },
  { value: 'branch_manager', label: 'Niederlassungsleiter/in (des Mitarbeiters)' },
  { value: 'executive', label: 'Prokurist/in oder Geschäftsführer/in' },
  { value: 'role', label: 'Bestimmte Rolle …' },
];

const newStep = () => ({ type: 'location_manager', role_id: null, label: '', require_all: true });

export const ApprovalChainCard = () => {
  const [mode, setMode] = useState('default'); // default | tree | custom
  const [treeRequireAll, setTreeRequireAll] = useState(false);
  const [treeIncludeExec, setTreeIncludeExec] = useState(true);
  const [steps, setSteps] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      companyAPI.getApprovalChain(),
      permissionsAPI.getAll().catch(() => ({ data: [] })),
    ]).then(([chainRes, rolesRes]) => {
      const d = chainRes.data || {};
      setMode(d.chain_mode || (d.use_custom_chain ? 'custom' : 'default'));
      setTreeRequireAll(!!d.tree_require_all);
      setTreeIncludeExec(d.tree_include_executives !== false);
      setSteps((d.steps || []).map((s) => ({
        type: s.type || 'location_manager',
        role_id: s.role_id || null,
        label: s.label || '',
        require_all: s.require_all !== false,
      })));
      setRoles(rolesRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const patchStep = (idx, patch) => setSteps((p) => p.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const removeStep = (idx) => setSteps((p) => p.filter((_, i) => i !== idx));
  const moveStep = (idx, dir) => setSteps((p) => {
    const j = idx + dir;
    if (j < 0 || j >= p.length) return p;
    const copy = [...p];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    return copy;
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        chain_mode: mode,
        use_custom_chain: mode === 'custom',
        tree_require_all: treeRequireAll,
        tree_include_executives: treeIncludeExec,
        steps: steps.map((s) => ({
          type: s.type,
          role_id: s.type === 'role' ? (s.role_id || null) : null,
          label: (s.label || '').trim() || null,
          require_all: !!s.require_all,
        })),
      };
      await companyAPI.updateApprovalChain(payload);
      toast.success('Freigabekette gespeichert');
    } catch {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="approval-chain-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <GitBranch className="h-5 w-5 text-primary" />
          Urlaubs-Freigabekette
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Legen Sie fest, über welche Stufen ein Urlaubsantrag genehmigt wird.
          Individuelle Ketten im Mitarbeiterprofil haben weiterhin Vorrang.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Laden…
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Freigabe-Modus</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="approval-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Standard (Standortleiter → Niederlassungsleiter)</SelectItem>
                  <SelectItem value="tree">Org-Baum (Freigabe läuft von unten nach oben)</SelectItem>
                  <SelectItem value="custom">Eigene Stufen-Kette</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Individuelle Ketten im Mitarbeiterprofil haben weiterhin Vorrang.
              </p>
            </div>

            {mode === 'tree' && (
              <div className="space-y-3 border rounded-md p-3 bg-muted/30" data-testid="approval-tree-options">
                <p className="text-sm text-muted-foreground">
                  Die Freigabe startet bei der Leitung des Knotens, an dem der Mitarbeiter
                  hängt, und läuft Ebene für Ebene nach oben durch den Org-Baum. Leitungen
                  je Knoten legen Sie in der Karte „Organisationsstruktur" fest.
                </p>
                <div className="flex items-center justify-between p-2 rounded bg-card">
                  <span className="text-xs">Pro Ebene müssen alle Leiter zustimmen (aus = eine Zustimmung genügt, z. B. Leiter ODER Stellvertreter)</span>
                  <Switch checked={treeRequireAll} onCheckedChange={setTreeRequireAll} data-testid="approval-tree-requireall" />
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-card">
                  <span className="text-xs">Geschäftsführung/Prokura als letzte Stufe anhängen</span>
                  <Switch checked={treeIncludeExec} onCheckedChange={setTreeIncludeExec} data-testid="approval-tree-exec" />
                </div>
              </div>
            )}

            {mode === 'custom' && (
              <div className="space-y-3">
                {steps.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Noch keine Stufe definiert.</p>
                )}
                {steps.map((s, idx) => (
                  <div key={idx} className="border rounded-md p-3 space-y-2 bg-card" data-testid={`approval-step-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Stufe {idx + 1}</span>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveStep(idx, -1)} disabled={idx === 0}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1}>
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => removeStep(idx)} data-testid={`approval-step-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Genehmiger-Typ</Label>
                        <Select value={s.type} onValueChange={(v) => patchStep(idx, { type: v })}>
                          <SelectTrigger data-testid={`approval-step-type-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STEP_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {s.type === 'role' && (
                        <div className="space-y-1.5">
                          <Label className="text-xs">Rolle</Label>
                          <Select value={s.role_id || ''} onValueChange={(v) => patchStep(idx, { role_id: v })}>
                            <SelectTrigger data-testid={`approval-step-role-${idx}`}>
                              <SelectValue placeholder="Rolle wählen…">
                                {roles.find((r) => r.id === s.role_id)?.name}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Bezeichnung (optional)</Label>
                      <Input
                        value={s.label}
                        onChange={(e) => patchStep(idx, { label: e.target.value })}
                        placeholder="Wird sonst automatisch aus dem Typ/der Rolle abgeleitet"
                        data-testid={`approval-step-label-${idx}`}
                      />
                    </div>

                    <div className="flex items-center justify-between p-2 rounded bg-muted/40">
                      <span className="text-xs">Alle Genehmiger müssen zustimmen (sonst reicht eine Zustimmung)</span>
                      <Switch checked={s.require_all} onCheckedChange={(v) => patchStep(idx, { require_all: v })} data-testid={`approval-step-requireall-${idx}`} />
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setSteps((p) => [...p, newStep()])} data-testid="approval-add-step">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Stufe hinzufügen
                </Button>
              </div>
            )}

            <Button onClick={handleSave} disabled={saving} data-testid="approval-chain-save-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Freigabekette speichern
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
